import { useEffect, useMemo, useState } from 'react';
import { DragDropContext, Droppable, Draggable, type DropResult } from '@hello-pangea/dnd';
import { ChevronDown, Inbox } from 'lucide-react';
import { useWorkItemStore } from '../../store/useWorkItemStore';
import { usePIStore } from '../../store/usePIStore';
import { useAppStore } from '../../store/useAppStore';
import { useAuthStore } from '../../store/useAuthStore';
import KanbanCard from './KanbanCard';
import { feature } from '../../services/telemetry';
import type { Sprint } from '../../types';

export default function SprintPlanningBoard() {
  const { items, updateItem } = useWorkItemStore();
  const { pis, getSprintsForPI } = usePIStore();
  const { setDetailItemId } = useAppStore();
  const { canEditWorkItem } = useAuthStore();
  const isDragEnabled = canEditWorkItem();

  // A board mode, not a route, so the view is counted here rather than by path.
  useEffect(() => { feature('sprint.planning'); }, []);

  const [selectedPIId, setSelectedPIId] = useState<string>(pis[0]?._id || '');
  const [showPIPicker, setShowPIPicker] = useState(false);

  const selectedPI = pis.find((p) => p._id === selectedPIId);
  const sprints: Sprint[] = useMemo(() => {
    if (!selectedPIId) return [];
    return getSprintsForPI(selectedPIId);
  }, [selectedPIId, getSprintsForPI]);

  // Build columns: Backlog + each sprint
  const columns = useMemo(() => {
    const cols: { id: string; label: string; sublabel: string; status?: string; sprint?: Sprint }[] = [
      { id: 'backlog', label: 'Backlog', sublabel: 'Unassigned items' },
    ];
    for (const sprint of sprints) {
      cols.push({
        id: sprint._id,
        label: sprint.name,
        sublabel: `${sprint.startDate} → ${sprint.endDate}`,
        status: sprint.status,
        sprint,
      });
    }
    return cols;
  }, [sprints]);

  // Only show stories in sprint planning
  const stories = useMemo(() => items.filter((i) => i.workItemType === 'story'), [items]);

  // Group stories by sprintId
  const columnItems = useMemo(() => {
    const map: Record<string, typeof items> = {};
    for (const col of columns) {
      map[col.id] = [];
    }
    for (const item of stories) {
      const sid = item.sprintId || '';
      if (sid && map[sid]) {
        map[sid].push(item);
      } else {
        map['backlog']?.push(item);
      }
    }
    // Sort each column by order
    for (const key of Object.keys(map)) {
      map[key].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }
    return map;
  }, [stories, columns]);

  const statusBadge = (status?: string) => {
    if (!status) return null;
    const colors =
      status === 'active' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
      : status === 'completed' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
      : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400';
    return (
      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${colors}`}>
        {status}
      </span>
    );
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;
    const { draggableId, destination } = result;
    const targetColId = destination.droppableId;

    // Find the sprint for the target column
    const targetSprint = sprints.find((s) => s._id === targetColId);

    const updates: Record<string, any> = {};
    if (targetColId === 'backlog') {
      updates.sprintId = '';
    } else if (targetSprint) {
      updates.sprintId = targetSprint._id;
      updates.startDate = targetSprint.startDate;
      updates.dueDate = targetSprint.endDate;
    }

    try {
      await updateItem(draggableId, updates);
    } catch {
      // Error handled by store
    }
  };

  if (pis.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-400 dark:text-gray-500 gap-3">
        <Inbox size={48} />
        <p className="text-lg">No Program Increments found</p>
        <p className="text-sm">Create a PI in Organization Admin to use Sprint Planning</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* PI Selector */}
      <div className="flex items-center gap-3 px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shrink-0">
        <span className="text-xs text-gray-500 dark:text-gray-400">PI:</span>
        <div className="relative">
          <button
            onClick={() => setShowPIPicker(!showPIPicker)}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm hover:border-accent/50 transition-colors"
          >
            <span className="text-gray-700 dark:text-gray-200">
              {selectedPI?.name || 'Select PI'}
            </span>
            <ChevronDown size={14} className="text-gray-400" />
          </button>
          {showPIPicker && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setShowPIPicker(false)} />
              <div className="absolute left-0 top-full mt-1 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-40 max-h-64 overflow-y-auto">
                {pis.map((pi) => (
                  <button
                    key={pi._id}
                    onClick={() => { setSelectedPIId(pi._id); setShowPIPicker(false); }}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                      selectedPIId === pi._id ? 'text-accent font-semibold bg-accent/5' : 'text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {pi.name}
                    <span className="text-[10px] text-gray-400 ml-2">{pi.startDate} → {pi.endDate}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="ml-auto text-xs text-gray-400 dark:text-gray-500">
          {stories.length} stories · {sprints.length} sprints
        </div>
      </div>

      {/* Sprint Columns */}
      <div className="flex flex-1 overflow-x-auto p-4 gap-4">
        <DragDropContext onDragEnd={handleDragEnd}>
          {columns.map((col) => {
            const colItems = columnItems[col.id] || [];
            return (
              <div
                key={col.id}
                className="flex flex-col shrink-0 w-72 bg-gray-100 dark:bg-gray-800 rounded-xl"
              >
                {/* Column header */}
                <div className="px-3 py-3 shrink-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 truncate">
                      {col.label}
                    </h3>
                    {statusBadge(col.status)}
                    <span className="text-xs text-gray-400 dark:text-gray-500 ml-auto">
                      {colItems.length}
                    </span>
                  </div>
                  <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">
                    {col.sublabel}
                  </p>
                </div>

                {/* Droppable area */}
                <Droppable droppableId={col.id}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`flex-1 overflow-y-auto px-2 pb-2 space-y-2 min-h-[100px] ${
                        snapshot.isDraggingOver ? 'bg-accent/5 rounded-b-xl' : ''
                      }`}
                    >
                      {colItems.map((item, index) => (
                        <Draggable
                          key={item._id}
                          draggableId={item._id}
                          index={index}
                          isDragDisabled={!isDragEnabled}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              onClick={() => setDetailItemId(item._id)}
                            >
                              <KanbanCard item={item} isDragging={snapshot.isDragging} />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
            );
          })}
        </DragDropContext>
      </div>
    </div>
  );
}
